import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BASE_URL } from '../../components/CommonExport.js';
import { DataGrid } from '@mui/x-data-grid';
import { TextField, Box, Typography, Paper, CircularProgress, Alert, IconButton, Grid, Autocomplete, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { Link } from 'react-router-dom';
import Component from '../../components/Component.js';
import DownloadIcon from '@mui/icons-material/Download';

const getTodayDate = () => {
    const now = new Date();
    const offsetMs = now.getTimezoneOffset() * 60000;
    const localDate = new Date(now.getTime() - offsetMs);
    return localDate.toISOString().split('T')[0];
};

const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const offsetMs = tomorrow.getTimezoneOffset() * 60000;
    const localDate = new Date(tomorrow.getTime() - offsetMs);
    return localDate.toISOString().split('T')[0];
};

const TopPosts = () => {
    const [topPosts, setTopPosts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [startDate, setStartDate] = useState(() => getTodayDate());
    const [endDate, setEndDate] = useState(() => getTomorrowDate());
    const [matchFilter, setMatchFilter] = useState(null);
    const [cidFilter, setCidFilter] = useState(null);
    const [mediaTypeFilter, setMediaTypeFilter] = useState('');
    const [matchData, setMatchData] = useState([]);
    const [competitionData, setCompetitionData] = useState([]);
    
    // Pagination state
    const [limit] = useState(10); // Number of items per page
    const [offset, setOffset] = useState(0);
    const [totalCount, setTotalCount] = useState(0);

    // Media type options
    const mediaTypes = [
        { label: 'All', value: null },
        { label: 'Image', value: 'image' },
        { label: 'Video', value: 'video/mp4' }
    ];

    // Calculate pagination info
    const totalPages = Math.ceil(totalCount / limit);
    const currentPage = Math.floor(offset / limit) + 1;
    
    // Fallback: if we have a full page of results, assume there might be more
    const hasMorePages = topPosts.length === limit;
    const canGoNext = totalCount > 0 ? currentPage < totalPages : hasMorePages;
    const canGoPrevious = currentPage > 1;

    // Fetch competitions data
    useEffect(() => {
        axios.get(`${BASE_URL}/admin/competitions/get_competitions?start_date=${startDate}&end_date=${endDate}`)
            .then((result) => {
                console.log(result.data.data.competitions)
                setCompetitionData(result.data.data.competitions);
            })
            .catch((error) => {
                console.log(error);
            });
    }, [startDate, endDate]);

    // Fetch matches data
    useEffect(() => {
        const fetchMatches = async () => {
            const requestBody = {};
            if (startDate) requestBody.startDate = startDate;
            if (endDate) requestBody.endDate = endDate;
            if (cidFilter) requestBody.cid = cidFilter;
            console.log(requestBody)

            try {
                const response = await axios.post(`${BASE_URL}/tracking/matches`, requestBody);
                console.log(response.data)
                setMatchData(response.data.data.users);
            } catch (error) {
                console.error('Error fetching matches:', error);
            }
        };

        fetchMatches();
    }, [startDate, endDate, cidFilter]);

    // Fetch top posts data
    useEffect(() => {
        const fetchTopPosts = async () => {
            setLoading(true);
            setError(null);
            
            try {
                const params = new URLSearchParams();
                if (startDate) params.append('sdate', startDate);
                if (endDate) params.append('edate', endDate);
                if (cidFilter) params.append('cid', cidFilter);
                if (matchFilter) params.append('mid', matchFilter.match_id);
                if (mediaTypeFilter) params.append('media', mediaTypeFilter);
                params.append('limit', limit);
                params.append('offset', offset);

                const response = await axios.get(`${BASE_URL}/admin/feeds/top/posts?${params.toString()}`);
                
                setTopPosts(response.data.data.result);
                
                // Handle total count - try different possible response structures
                const total = response.data.data.totalCount
                setTotalCount(total);
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to fetch data');
            } finally {
                setLoading(false);
            }
        };

        fetchTopPosts();
    }, [startDate, endDate, matchFilter, cidFilter, mediaTypeFilter, limit, offset]);

    // Reset offset when filters change
    useEffect(() => {
        setOffset(0);
    }, [startDate, endDate, matchFilter, cidFilter, mediaTypeFilter]);

    const handleNextPage = () => {
        console.log('Next clicked - Current page:', currentPage, 'Total pages:', totalPages);
        if (currentPage < totalPages) {
            setOffset(offset + limit);
        }
    };

    const handlePreviousPage = () => {
        console.log('Previous clicked - Current page:', currentPage);
        if (currentPage > 1) {
            setOffset(offset - limit);
        }
    };

    const handleExport = async () => {
        if (!startDate || !endDate) {
            setError('Please select both start and end dates for export.');
            return;
        }

        try {
            const params = new URLSearchParams();
            params.append('sdate', startDate);
            params.append('edate', endDate);
            if (cidFilter) params.append('cid', cidFilter);
            if (matchFilter) params.append('mid', matchFilter.match_id);
            if (mediaTypeFilter) params.append('media', mediaTypeFilter);
            params.append('export', 'true');

            console.log('Exporting with params:', params.toString());
            console.log('Full URL:', `${BASE_URL}/admin/feeds/top/posts?${params.toString()}`);

            const response = await axios.get(`${BASE_URL}/admin/feeds/top/posts?${params.toString()}`, {
                responseType: 'blob',
            });

            console.log('Response headers:', response.headers);
            console.log('Response data type:', typeof response.data);
            console.log('Response data size:', response.data.size || response.data.length);

            // Check if we got actual Excel data
            if (response.data.size === 0) {
                setError('Export returned empty data. Please check your date range.');
                return;
            }

            // Try to determine the correct file extension from content-type header
            let fileExtension = 'xlsx';
            const contentType = response.headers['content-type'];
            if (contentType) {
                if (contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
                    fileExtension = 'xlsx';
                } else if (contentType.includes('application/vnd.ms-excel')) {
                    fileExtension = 'xls';
                } else if (contentType.includes('text/csv')) {
                    fileExtension = 'csv';
                } else if (contentType.includes('application/json')) {
                    // If it's JSON, we need to handle it differently
                    const reader = new FileReader();
                    reader.onload = () => {
                        try {
                            const jsonData = JSON.parse(reader.result);
                            console.log('Received JSON data:', jsonData);
                            setError('API returned JSON instead of Excel. Please check if export=true is supported.');
                        } catch (e) {
                            setError('Unexpected response format from API');
                        }
                    };
                    reader.readAsText(response.data);
                    return;
                }
            }

            // Create blob with proper MIME type
            const blob = new Blob([response.data], { 
                type: contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `top_posts_${startDate}_to_${endDate}.${fileExtension}`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            setError(null); // Clear any previous errors

        } catch (err) {
            console.error('Export error:', err);
            if (err.response) {
                console.error('Response data:', err.response.data);
                console.error('Response headers:', err.response.headers);
                
                // Try to read the error response as text
                if (err.response.data instanceof Blob) {
                    const reader = new FileReader();
                    reader.onload = () => {
                        console.log('Error response content:', reader.result);
                        setError(`Export failed: ${reader.result}`);
                    };
                    reader.readAsText(err.response.data);
                } else {
                    setError(err.response.data?.message || 'Failed to export data');
                }
            } else {
                setError('Failed to export data: ' + err.message);
            }
        }
    };

    const columns = [
        {
            field: 'created_at_ist', 
            headerName: 'Created At (IST)', 
            width: 200,
            valueFormatter: (params) => {
                if (!params.value) return '';
                const date = new Date(params.value);
                return date.toLocaleString('en-IN', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    timeZone: 'Asia/Kolkata'
                });
            }
        },
        {
            field: 'media_type', 
            headerName: 'Media Type', 
            width: 150,
            valueFormatter: (params) => {
                if (!params.value) return 'Text';
                return params.value === 'image' ? 'Image' : 
                       params.value === 'video/mp4' ? 'Video' : 
                       params.value;
            }
        },
        {field: 'content', headerName: 'Content', width: 200},
        {field: 'likes_count', headerName: 'Total Likes', width: 200},
        {field: 'comments_count', headerName: 'Total Comments', width: 200},
        {field: 'total_engagement', headerName: 'Total Engagement', width: 200},
        {field: 'total_feed_views', headerName: 'Total Feed Views', width: 200},
        {field: 'unique_feed_views', headerName: 'Unique Feed Views', width: 200},
        {field: 'username', headerName: 'Username', width: 200},
        {field: 'copy_url', headerName: 'Copy URL', width: 200}
    ];

    return (
        <Component>
            <div className="page-body">
                <div className="container-fluid">
                    <div className="page-header">
                        <div className="row">
                            <div className="col-sm-12">
                                <div className="d-flex align-items-center">
                                    <IconButton style={{ background: '#FFFFFF' }} component={Link} to="/">
                                        <ArrowBackIcon />
                                    </IconButton>
                                    <h3 style={{ marginLeft: '20px' }}>Top Posts</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="container-fluid">
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card">
                                <div className="card-body">
                                    <Paper elevation={3} style={{ padding: '20px' }}>
                                        <Grid container spacing={2} sx={{ mb: 3 }}>
                                            <Grid item xs={12} sm={6} md={2}>
                                                <TextField
                                                    fullWidth
                                                    type="date"
                                                    label="From Date"
                                                    value={startDate}
                                                    onChange={(e) => setStartDate(e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={2}>
                                                <TextField
                                                    fullWidth
                                                    type="date"
                                                    label="To Date"
                                                    value={endDate}
                                                    onChange={(e) => setEndDate(e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={2}>
                                                <Autocomplete
                                                    options={competitionData}
                                                    getOptionLabel={(option) => option.title || ''}
                                                    isOptionEqualToValue={(option, value) => option.cid === value.cid}
                                                    value={competitionData.find(comp => comp.cid === cidFilter) || null}
                                                    onChange={(event, newValue) => {
                                                        setCidFilter(newValue ? newValue.cid : null);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField {...params} label="Competition" />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={2}>
                                                <Autocomplete
                                                    options={matchData}
                                                    getOptionLabel={(option) => option.title + ' - ' + option.subtitle + ' - ' + option.match_id || ''}
                                                    isOptionEqualToValue={(option, value) => option.match_id === value.match_id}
                                                    value={matchFilter}
                                                    onChange={(event, newValue) => {
                                                        setMatchFilter(newValue);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField {...params} label="Match" />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={2}>
                                                <Autocomplete
                                                    options={mediaTypes}
                                                    getOptionLabel={(option) => option.label}
                                                    value={mediaTypes.find(type => type.value === mediaTypeFilter) || mediaTypes[0]}
                                                    onChange={(event, newValue) => {
                                                        setMediaTypeFilter(newValue ? newValue.value : '');
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField {...params} label="Media Type" />
                                                    )}
                                                />
                                            </Grid>
                                        </Grid>

                                        {/* Export Button */}
                                        <Grid container spacing={2} sx={{ mb: 3 }}>
                                            <Grid item xs={12} sm={6} md={2}>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={handleExport}
                                                    disabled={!startDate || !endDate}
                                                    startIcon={<DownloadIcon />}
                                                    fullWidth
                                                >
                                                    Export Data
                                                </Button>
                                            </Grid>
                                        </Grid>

                                        {loading ? (
                                            <Box display="flex" justifyContent="center" mt={3}>
                                                <CircularProgress />
                                            </Box>
                                        ) : error ? (
                                            <Alert severity="error" mt={3}>
                                                {error}
                                            </Alert>
                                        ) : (
                                            <>
                                                <Box mt={3} style={{ height: 400 }}>
                                                    <DataGrid
                                                        rows={topPosts}
                                                        columns={columns}
                                                        pageSize={limit}
                                                        rowsPerPageOptions={[limit]}
                                                        disableSelectionOnClick
                                                        hideFooterPagination={true}
                                                    />
                                                </Box>
                                                
                                                {/* Pagination Controls */}
                                                <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                                                    <Typography variant="body2" color="textSecondary">
                                                        {totalCount > 0 
                                                            ? `Showing ${offset + 1} to ${Math.min(offset + limit, totalCount)} of ${totalCount} results`
                                                            : `Showing ${offset + 1} to ${offset + topPosts.length} results`
                                                        }
                                                    </Typography>
                                                    
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <Button
                                                            variant="outlined"
                                                            startIcon={<NavigateBeforeIcon />}
                                                            onClick={handlePreviousPage}
                                                            disabled={!canGoPrevious}
                                                        >
                                                            Previous
                                                        </Button>
                                                        
                                                        <Typography variant="body2" sx={{ mx: 2 }}>
                                                            Page {currentPage} {totalCount > 0 ? `of ${totalPages}` : ''}
                                                        </Typography>
                                                        
                                                        <Button
                                                            variant="outlined"
                                                            endIcon={<NavigateNextIcon />}
                                                            onClick={handleNextPage}
                                                            disabled={!canGoNext}
                                                        >
                                                            Next
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            </>
                                        )}
                                    </Paper>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Component>
    );
};

export default TopPosts;
